/* eslint-disable react-hooks/exhaustive-deps */
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ArticleIcon from "@mui/icons-material/Article";
import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import {
  DataGridPro,
  GridToolbarContainer,
} from "@mui/x-data-grid-pro";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { esArray } from "src/general_components/functionEsArray";
import { SimpleDialogComponent } from "src/general_components/SimpleDialogComponent";
import { SnackbarMessenger } from "src/general_components/SnackbarMessenger";
import { getCuentasJuridicas } from "src/projects/gestion_comercial/helpers/CuentasJuridicasData";
import { getResumenCuenta } from "../helpers/ResumenCuentaData";

// ---------- helpers de fecha ----------
const primerDiaMes = () => dayjs().startOf("month");
const hoy = () => dayjs();

// ---------- columnas del DataGrid ----------
const columns = [
  { field: "nombre", headerName: "Servicio/Item", flex: 2, minWidth: 180 },
  {
    field: "cantidad",
    headerName: "Cantidad",
    flex: 0.6,
    minWidth: 100,
    type: "number",
    align: "right",
    headerAlign: "right",
  },
  {
    field: "importe",
    headerName: "Importe",
    flex: 0.8,
    minWidth: 130,
    type: "number",
    align: "right",
    headerAlign: "right",
    valueFormatter: ({ value }) =>
      value !== undefined
        ? "$ " +
          parseFloat(value).toLocaleString("es-AR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
        : "",
  },
];

// ---------- toolbar personalizado ----------
function ResumenToolbar({ onCsvExport, onExcelExport, onPdfExport }) {
  return (
    <GridToolbarContainer>
      <Button size="small" startIcon={<DownloadIcon />} onClick={onCsvExport}>
        CSV
      </Button>
      <Button size="small" startIcon={<ArticleIcon />} onClick={onExcelExport}>
        Excel
      </Button>
      <Button size="small" startIcon={<PictureAsPdfIcon />} onClick={onPdfExport}>
        PDF
      </Button>
    </GridToolbarContainer>
  );
}

// ---------- formulario de filtros (dentro del modal) ----------
function FiltrosForm({ clienteOptions, filtros, setFiltros, onBuscar }) {
  const [clienteError, setClienteError] = useState(false);
  const [fechaDesdeError, setFechaDesdeError] = useState(false);
  const [fechaHastaError, setFechaHastaError] = useState(false);

  const handleBuscar = () => {
    let ok = true;
    if (!filtros.cliente) { setClienteError(true); ok = false; }
    if (!filtros.fechaDesde || !filtros.fechaDesde.isValid()) { setFechaDesdeError(true); ok = false; }
    if (!filtros.fechaHasta || !filtros.fechaHasta.isValid()) { setFechaHastaError(true); ok = false; }
    if (ok) onBuscar();
  };

  return (
    <Grid container spacing={2} sx={{ pt: 1, minWidth: 360 }}>
      <Grid item xs={12}>
        <Autocomplete
          options={clienteOptions}
          value={filtros.cliente}
          onChange={(_, value) => {
            setFiltros((f) => ({ ...f, cliente: value }));
            setClienteError(false);
          }}
          getOptionLabel={(o) => o.label ?? ""}
          isOptionEqualToValue={(o, v) => o.value === v.value}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Cliente *"
              error={clienteError}
              helperText={clienteError ? "Campo obligatorio" : ""}
              size="small"
            />
          )}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Fecha desde *"
            inputFormat="DD/MM/YYYY"
            value={filtros.fechaDesde}
            onChange={(val) => {
              setFiltros((f) => ({ ...f, fechaDesde: val }));
              setFechaDesdeError(false);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                fullWidth
                error={fechaDesdeError}
                helperText={fechaDesdeError ? "Campo obligatorio" : ""}
              />
            )}
          />
        </LocalizationProvider>
      </Grid>

      <Grid item xs={12} sm={6}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Fecha hasta *"
            inputFormat="DD/MM/YYYY"
            value={filtros.fechaHasta}
            onChange={(val) => {
              setFiltros((f) => ({ ...f, fechaHasta: val }));
              setFechaHastaError(false);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                fullWidth
                error={fechaHastaError}
                helperText={fechaHastaError ? "Campo obligatorio" : ""}
              />
            )}
          />
        </LocalizationProvider>
      </Grid>

      <Grid item xs={12} sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button variant="contained" onClick={handleBuscar}>
          Buscar
        </Button>
      </Grid>
    </Grid>
  );
}

// ---------- componente principal ----------
export const ResumenCuentaList = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [clienteOptions, setClienteOptions] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

  const [filtros, setFiltros] = useState({
    cliente: null,
    fechaDesde: primerDiaMes(),
    fechaHasta: hoy(),
  });

  // Carga el listado de clientes al montar
  useEffect(() => {
    const cargarClientes = async () => {
      const res = esArray(await getCuentasJuridicas());
      const opciones = res.map((c) => ({
        ...c,
        label: c.id_cuentas + " - " + c.razon_social,
        value: c.id_cuentas,
      }));
      setClienteOptions(opciones);
    };
    cargarClientes();
  }, []);

  const handleBuscar = async () => {
    setModalOpen(false);
    setLoading(true);
    try {
      const data = await getResumenCuenta({
        id_cuentas: filtros.cliente.value,
        fecha_desde: filtros.fechaDesde.format("YYYY-MM-DD"),
        fecha_hasta: filtros.fechaHasta.format("YYYY-MM-DD"),
      });
      setRows(data);
      if (data.length === 0) {
        setSnackbar({ open: true, message: "No se encontraron movimientos para los filtros seleccionados.", severity: "info" });
      }
    } catch {
      setSnackbar({ open: true, message: "Ocurrió un error al consultar el servicio.", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  // Filas de encabezado comunes a ambos exports
  const buildInfoHeader = () => ({
    clienteLabel: filtros.cliente?.label ?? "",
    periodoDesde: filtros.fechaDesde ? filtros.fechaDesde.format("DD/MM/YYYY") : "",
    periodoHasta: filtros.fechaHasta ? filtros.fechaHasta.format("DD/MM/YYYY") : "",
  });

  const handlePdfExport = async () => {
    if (rows.length === 0) return;
    try {
      const { default: jsPDF } = await import("jspdf");
      await import("jspdf-autotable");
      const { clienteLabel, periodoDesde, periodoHasta } = buildInfoHeader();

      const doc = new jsPDF({ orientation: "landscape" });

      // Encabezado informativo
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.text("Resumen de cuenta", 14, 14);

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text(`Cliente: ${clienteLabel}`, 14, 22);
      doc.text(`Período: ${periodoDesde} - ${periodoHasta}`, 14, 28);

      // Tabla de datos
      doc.autoTable({
        startY: 34,
        head: [["Servicio/Item", "Cantidad", "Importe"]],
        body: rows.map((r) => [
          r.nombre,
          r.cantidad,
          parseFloat(r.importe).toLocaleString("es-AR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }),
        ]),
        headStyles: { fillColor: [41, 98, 155], textColor: 255, fontStyle: "bold" },
        alternateRowStyles: { fillColor: [240, 245, 255] },
        columnStyles: {
          0: { cellWidth: "auto" },
          1: { halign: "right", cellWidth: 30 },
          2: { halign: "right", cellWidth: 40 },
        },
        styles: { fontSize: 9, cellPadding: 3 },
      });

      doc.save("resumen-cuenta.pdf");
    } catch {
      setSnackbar({ open: true, message: "Error al generar el archivo PDF.", severity: "error" });
    }
  };

  const handleCsvExport = () => {
    if (rows.length === 0) return;
    const { clienteLabel, periodoDesde, periodoHasta } = buildInfoHeader();

    const escapeCsv = (val) => `"${String(val ?? "").replace(/"/g, '""')}"`;

    const lines = [
      `${escapeCsv("Cliente")}${escapeCsv(clienteLabel)}`,
      `${escapeCsv("Período")}${escapeCsv(`${periodoDesde} - ${periodoHasta}`)}`,
      "",
      [escapeCsv("Servicio/Item"), escapeCsv("Cantidad"), escapeCsv("Importe")].join(","),
      ...rows.map((r) =>
        [escapeCsv(r.nombre), escapeCsv(r.cantidad), escapeCsv(r.importe)].join(",")
      ),
    ];

    const bom = "\uFEFF";
    const blob = new Blob([bom + lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "resumen-cuenta.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExcelExport = async () => {
    if (rows.length === 0) return;
    try {
      const ExcelJS = (await import("exceljs")).default;
      const { saveAs } = await import("file-saver");
      const { clienteLabel, periodoDesde, periodoHasta } = buildInfoHeader();

      const workbook = new ExcelJS.Workbook();
      const ws = workbook.addWorksheet("Resumen de cuenta");

      // Filas de encabezado informativo
      const rowCliente = ws.addRow(["Cliente", clienteLabel]);
      rowCliente.font = { bold: true };

      const rowPeriodo = ws.addRow(["Período", `${periodoDesde} - ${periodoHasta}`]);
      rowPeriodo.font = { bold: true };

      ws.addRow([]); // fila en blanco separadora

      // Cabecera de columnas
      const headerRow = ws.addRow(["Servicio/Item", "Cantidad", "Importe"]);
      headerRow.font = { bold: true };
      headerRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFD9E1F2" } };

      // Datos
      rows.forEach((r) => ws.addRow([r.nombre, r.cantidad, r.importe]));

      // Anchos y formato numérico de importe
      ws.getColumn(1).width = 40;
      ws.getColumn(2).width = 15;
      ws.getColumn(3).width = 20;
      ws.getColumn(3).numFmt = '#,##0.00';

      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer], { type: "application/octet-stream" }), "resumen-cuenta.xlsx");
    } catch {
      setSnackbar({ open: true, message: "Error al generar el archivo Excel.", severity: "error" });
    }
  };

  return (
    <>
      {/* Botón Filtros */}
      <Grid container justifyContent="flex-end" sx={{ mb: 1, mt: 1 }}>
        <Button
          variant="contained"
          endIcon={<FilterAltIcon />}
          onClick={() => setModalOpen(true)}
        >
          Filtros
        </Button>
      </Grid>

      {/* Modal de filtros */}
      {modalOpen && (
        <SimpleDialogComponent
          open={modalOpen}
          modalName="Filtros de búsqueda"
          child={
            <FiltrosForm
              clienteOptions={clienteOptions}
              filtros={filtros}
              setFiltros={setFiltros}
              onBuscar={handleBuscar}
            />
          }
          handleClose={() => setModalOpen(false)}
          buttonEnviar={false}
          maxWidth="sm"
        />
      )}

      {/* Grilla */}
      <Box sx={{ width: "100%" }}>
        {rows.length === 0 && !loading ? (
          <Box
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              p: 4,
              textAlign: "center",
              color: "#9e9e9e",
            }}
          >
            <Typography variant="body1">
              Utilice los filtros para buscar movimientos.
            </Typography>
          </Box>
        ) : (
          <DataGridPro
            rows={rows}
            columns={columns}
            loading={loading}
            autoHeight
            disableRowSelectionOnClick
            hideFooterPagination
            hideFooterSelectedRowCount
            components={{ Toolbar: ResumenToolbar }}
            componentsProps={{ toolbar: { onCsvExport: handleCsvExport, onExcelExport: handleExcelExport, onPdfExport: handlePdfExport } }}
            sx={{
              "& .MuiDataGrid-columnHeaders": { backgroundColor: "#f5f5f5" },
              "& .MuiDataGrid-row:hover": { backgroundColor: "#fafafa" },
            }}
          />
        )}
      </Box>

      <SnackbarMessenger
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        close={() => setSnackbar((s) => ({ ...s, open: false }))}
      />
    </>
  );
};
